
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurl, baseurImage } from "../../Baseurl";
import BarcodeGenerator from "../BarcodeGenerator/BarcodeGenerator";
import axiosInstance from "../../api/axiosInstance";

export default function ViewInventory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState(null);
  const [batches, setBatches] = useState([]);
  const [movements, setMovements] = useState([]);

  useEffect(() => {
    axiosInstance
      .get(`/getDrugById/${id}`)
      .then((res) => {
        if (res.data.success) setInventory(res.data.data);
      })
      .catch((err) => console.error("Fetch error:", err));

    axiosInstance.get(`getBatchesByDrug/${id}`)
      .then(res => setBatches(res.data.data || []))
      .catch(() => {});

    axiosInstance.get(`getStockMovementsByDrug/${id}`)
      .then(res => setMovements((res.data.data || []).slice(0, 10)))
      .catch(() => {});
  }, [id]);


  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (!inventory) return null;

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">
            <div className="tableHeader">
              <h5 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{ cursor: 'pointer', color: '#3f75c7', fontWeight: 'bold', fontSize: '1.5rem' }}
                  onClick={() => navigate(-1)}
                  title="Back"
                >
                  &larr;
                </span>
                Inventory Item Details
              </h5>
            </div>
            <div className="card-body" style={{ margin: "15px" }}>
              <div className="row justify-content-center mb-4">
                {inventory.image && (
                  <div className="col-md-4 text-center">
                    <img
                      src={`${baseurImage}${inventory.image}`}
                      alt={inventory.name}
                      className="img-fluid rounded"
                      style={{ maxHeight: "250px", objectFit: "contain" }}
                    />
                  </div>
                )}
              </div>

              <div className="row">
                {[
                  ["Name", inventory.name],
                  ["Substance", inventory.substance],
                  ["Strength", inventory.strength],
                  ["Unit", inventory.unit_of_measurement],
                  ["Company", inventory.company],
                  ["Quantity", inventory.quantity],
                  ["Expiry", formatDate(inventory.expiration_date)],
                  ["Cost", inventory.cost],
                  ["Price", inventory.price],
                  ["Control", inventory.control === 1 ? "Yes" : "No"],
                  ["VAT", inventory.vat || "No"],
                  ["VAT Value", inventory.vat === "Yes" ? `${inventory.vat_value}%` : "0%"],
                  ["Reorder Level", inventory.reorder_level ?? 10],
                ].map(([label, value], idx) => (
                  <div className="col-md-6 mb-3" key={idx}>
                    <label className="form-label">{label}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={value ?? ""}
                      disabled
                    />
                  </div>
                ))}

                {/* Barcode Display */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Barcode</label>
                  {inventory.barcode && (
                    <div className="text-center">
                      <div style={{ maxWidth: '260px', margin: '0 auto' }}>
                        <BarcodeGenerator value={String(inventory.barcode)} width={2} height={80} />
                      </div>
                      <div className="mt-2" style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6c757d' }}>
                        {inventory.barcode}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Batches Section */}
              {batches.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-3">Active Batches</h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Batch #</th>
                          <th>Mfg Date</th>
                          <th>Expiry Date</th>
                          <th>Qty Received</th>
                          <th>Qty Remaining</th>
                          <th>Cost/Unit</th>
                          <th>Vendor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batches.map(b => {
                          const isExpired = b.expiry_date && new Date(b.expiry_date) < new Date();
                          return (
                            <tr key={b.id}>
                              <td><code>{b.batch_number}</code></td>
                              <td>{b.manufacture_date?.slice(0, 10) || "—"}</td>
                              <td style={{ color: isExpired ? "red" : "inherit" }}>
                                {b.expiry_date?.slice(0, 10) || "—"}
                                {isExpired && <span className="badge bg-danger ms-1">Expired</span>}
                              </td>
                              <td>{b.quantity_received}</td>
                              <td><strong>{b.quantity_remaining}</strong></td>
                              <td>{b.cost_per_unit || "—"}</td>
                              <td>{b.vendor_name || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Stock History Section */}
              {movements.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-3">Recent Stock Movements (last 10)</h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th>
                          <th>Quantity</th>
                          <th>Batch #</th>
                          <th>Reference</th>
                          <th>Notes</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movements.map(m => (
                          <tr key={m.id}>
                            <td>
                              <span className={`badge bg-${m.quantity > 0 ? "success" : "danger"}`}>
                                {m.movement_type?.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td style={{ color: m.quantity > 0 ? "green" : "red", fontWeight: 600 }}>
                              {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                            </td>
                            <td>{m.batch_number ? <code>{m.batch_number}</code> : "—"}</td>
                            <td>{m.reference_type ? `${m.reference_type.replace(/_/g, " ")} ${m.reference_id ? "#" + m.reference_id : ""}` : "—"}</td>
                            <td>{m.notes || "—"}</td>
                            <td>{new Date(m.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="text-end">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
